import Axios from 'axios';

import {
    ARTICLE_BLOCK_TYPE,
    ARTICLE_FILE_LINK_TEXT_DEFAULT,
    ARTICLE_MEDIA_SOURCE,
    ARTICLE_MEDIA_TYPE,
    ARTICLE_ROW_LAYOUT,
    blockTypeHasMedia,
    buildArticleSearchText,
    getArticleRowLayoutColumnCount,
    inferArticleRowLayoutFromColumnCount,
    normalizeArticleBlockType,
    normalizeArticleColumnCount,
    normalizeArticleRowLayoutKey,
    sanitizeArticleSlugInput,
} from '@containers/Art/Articles/Articles.constants';
import {
    getArtDocumentPlaybackUrl,
    getArtDocumentPreviewUrl,
    getArtDocumentSource,
    getArtMediaDocumentId,
    getArtStoredMediaType,
    resolveArtDocumentFileUrl,
} from '@containers/Art/ArtDocuments';

export const ARTICLES_SAVE_MODE = {
    API: 'api',
};

/** Thrown by {@link removeArticle} when one or more news items link to this article as target. */
export const ARTICLE_REMOVE_BLOCKED_BY_NEWS = 'ARTICLE_REMOVE_BLOCKED_BY_NEWS';

const ARTICLES_API_BASE = '/cms/newsarticles';
const ARTICLES_SEARCH_API = '/cms/newsarticles/search';

const makeLocalID = () => {
    return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizeTitle = (value) => {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ');
};

const getSafePosition = (position, fallbackPosition) => {
    const numericPosition = Number(position);
    return Number.isFinite(numericPosition) ? numericPosition : fallbackPosition;
};

const getArticleMediaFileUrl = (media = {}, type) => {
    const mediaList = media.Media || media.media;
    if (!Array.isArray(mediaList)) return undefined;

    const mediaFile = mediaList.find(
        (item) => String(item.Type || item.type || '').toLowerCase() === String(type).toLowerCase()
    );

    return resolveArtDocumentFileUrl(mediaFile?.Url || mediaFile?.url);
};

const getArticleMediaExplicitUrl = (media = {}) => {
    return resolveArtDocumentFileUrl(
        media.Url ?? media.url ?? media.MediaUrl ?? media.mediaUrl ?? media.PlaybackUrl ?? media.playbackUrl
    );
};

const getArticleMediaType = (media = {}) => {
    // Linked files aren't image/video: bypass the generic (image/video-only) inference below,
    // which would otherwise mistake a PDF's explicit Url for an image.
    if (String(media.Type || media.type || '').toLowerCase() === ARTICLE_MEDIA_TYPE.FILE) {
        return ARTICLE_MEDIA_TYPE.FILE;
    }

    const explicitType = getArtStoredMediaType(media);
    if (explicitType) return explicitType;

    if (media.Source === ARTICLE_MEDIA_SOURCE.EMBED || media.source === ARTICLE_MEDIA_SOURCE.EMBED) {
        return ARTICLE_MEDIA_TYPE.VIDEO;
    }

    return undefined;
};

const getArticleMediaUrl = (media = {}, mediaType) => {
    const explicitUrl = getArticleMediaExplicitUrl(media);
    if (explicitUrl) return explicitUrl;

    if (mediaType === ARTICLE_MEDIA_TYPE.VIDEO) {
        return (
            getArtDocumentPlaybackUrl(media) ||
            getArticleMediaFileUrl(media, 'HD') ||
            getArticleMediaFileUrl(media, 'Standard') ||
            getArticleMediaFileUrl(media, 'Thumbnail') ||
            getArtDocumentPreviewUrl(media)
        );
    }

    return (
        getArtDocumentPreviewUrl(media) ||
        getArticleMediaFileUrl(media, 'Standard') ||
        getArticleMediaFileUrl(media, 'HD') ||
        getArticleMediaFileUrl(media, 'Thumbnail') ||
        getArtDocumentPlaybackUrl(media)
    );
};

export const makeArticleElementId = () => {
    return makeLocalID();
};

export const makeArticleMedia = (media = {}) => {
    const legacyDocumentId =
        media.DocumentID ??
        media.documentId ??
        media.MediaId ??
        media.mediaId ??
        media.LocalMediaId ??
        media.localMediaId;
    const mediaType = getArticleMediaType(media);
    const mediaUrl = getArticleMediaUrl(media, mediaType);
    const source = media.Source || media.source || getArtDocumentSource(media) || ARTICLE_MEDIA_SOURCE.UPLOAD;

    const articleMedia = {
        DocumentID: legacyDocumentId,
        EmbedInput: media.EmbedInput || media.embedInput,
        ModelId: media.ModelId || media.modelId,
        Source: source,
    };

    if (mediaType) articleMedia.Type = mediaType;
    if (mediaUrl) articleMedia.Url = mediaUrl;
    if (mediaType === ARTICLE_MEDIA_TYPE.FILE) {
        articleMedia.LinkText = String(media.LinkText ?? media.linkText ?? '').trim() || ARTICLE_FILE_LINK_TEXT_DEFAULT;
    }

    return articleMedia;
};

export const makePersistedArticleMedia = (media = {}) => {
    const documentId = getArtMediaDocumentId(media);
    if (!documentId) return makeArticleMedia();

    const normalizedMedia = makeArticleMedia(media);
    const persistedMedia = {
        DocumentID: documentId,
    };

    if (normalizedMedia.Source) persistedMedia.Source = normalizedMedia.Source;
    if (normalizedMedia.Type) persistedMedia.Type = normalizedMedia.Type;
    if (normalizedMedia.Url) persistedMedia.Url = normalizedMedia.Url;
    if (normalizedMedia.Type === ARTICLE_MEDIA_TYPE.FILE) persistedMedia.LinkText = normalizedMedia.LinkText;

    return persistedMedia;
};

export const makeArticleBlock = (type = ARTICLE_BLOCK_TYPE.TEXT, position = 1) => {
    const blockType = normalizeArticleBlockType(type);
    const initialMedia =
        blockType === ARTICLE_BLOCK_TYPE.FILE
            ? makeArticleMedia({ LinkText: ARTICLE_FILE_LINK_TEXT_DEFAULT, Type: ARTICLE_MEDIA_TYPE.FILE })
            : blockType === ARTICLE_BLOCK_TYPE.MEDIA
              ? makeArticleMedia()
              : undefined;

    return {
        ID: makeArticleElementId(),
        Media: initialMedia,
        Position: position,
        Text: '',
        Title: '',
        Type: blockType,
    };
};

export const makeArticleColumn = (position = 1) => {
    return {
        Blocks: [],
        ID: makeArticleElementId(),
        Position: position,
    };
};

export const makeArticleRow = (layoutKey = ARTICLE_ROW_LAYOUT.ONE_COL, position = 1) => {
    const layout = normalizeArticleRowLayoutKey(
        layoutKey ?? ARTICLE_ROW_LAYOUT.ONE_COL,
        ARTICLE_ROW_LAYOUT.ONE_COL
    );
    const columnCount = getArticleRowLayoutColumnCount(layout);

    return {
        ColumnCount: columnCount,
        Columns: Array.from({ length: columnCount }, (item, index) => makeArticleColumn(index + 1)),
        ID: makeArticleElementId(),
        Layout: layout,
        Position: position,
    };
};

export const makeUploadArticleMedia = ({ document, documentId }) => {
    const resolvedDocumentId = documentId ?? document?.ID ?? document?.id;

    return makeArticleMedia({
        ...(document || {}),
        DocumentID: resolvedDocumentId,
        Source: ARTICLE_MEDIA_SOURCE.UPLOAD,
    });
};

export const makeFileArticleMedia = ({ document, documentId, linkText }) => {
    const resolvedDocumentId = documentId ?? document?.ID ?? document?.id;

    return makeArticleMedia({
        ...(document || {}),
        DocumentID: resolvedDocumentId,
        LinkText: linkText,
        Source: ARTICLE_MEDIA_SOURCE.UPLOAD,
        Type: ARTICLE_MEDIA_TYPE.FILE,
    });
};

export const makeNetwalkArticleMedia = ({ media, modelId }) => {
    return makeArticleMedia({
        ...(media.original || {}),
        ...media,
        DocumentID: media.ID,
        ModelId: modelId,
        Source: ARTICLE_MEDIA_SOURCE.NETWALK,
    });
};

export const makeEmbedArticleMedia = ({ document, documentId }) => {
    const resolvedDocumentId = documentId ?? document?.ID ?? document?.id;

    return makeArticleMedia({
        ...(document || {}),
        DocumentID: resolvedDocumentId,
        Source: ARTICLE_MEDIA_SOURCE.EMBED,
    });
};

const normalizeArticleMedia = (media = {}) => makeArticleMedia(media);

const normalizeArticleBlock = (block = {}, index = 0) => {
    const blockType = normalizeArticleBlockType(block.Type || block.type);

    return {
        ID: block.ID || makeArticleElementId(),
        Media: blockTypeHasMedia(blockType) ? normalizeArticleMedia(block.Media || block.media || {}) : undefined,
        Position: getSafePosition(block.Position, index + 1),
        Text: String(block.Text || block.text || ''),
        Title: String(block.Title || block.title || ''),
        Type: blockType,
    };
};

const normalizeArticleBlocks = (blocks = []) => {
    return [...blocks]
        .map(normalizeArticleBlock)
        .sort((firstBlock, secondBlock) => firstBlock.Position - secondBlock.Position)
        .map((block, index) => ({
            ...block,
            Position: index + 1,
        }));
};

const normalizeArticleColumn = (column = {}, index = 0) => {
    return {
        Blocks: normalizeArticleBlocks(column.Blocks || column.blocks || []),
        ID: column.ID || makeArticleElementId(),
        Position: getSafePosition(column.Position, index + 1),
    };
};

const normalizeArticleColumns = (columns = [], columnCount = 1) => {
    const normalizedColumns = [...columns]
        .map(normalizeArticleColumn)
        .sort((firstColumn, secondColumn) => firstColumn.Position - secondColumn.Position)
        .slice(0, columnCount)
        .map((column, index) => ({
            ...column,
            Position: index + 1,
        }));

    while (normalizedColumns.length < columnCount) {
        normalizedColumns.push(makeArticleColumn(normalizedColumns.length + 1));
    }

    return normalizedColumns;
};

const normalizeArticleRow = (row = {}, index = 0) => {
    const columnsRaw = row.Columns || row.columns || [];
    const countGuess = normalizeArticleColumnCount(
        row.ColumnCount || row.columnCount || columnsRaw.length || 1
    );
    const layout = normalizeArticleRowLayoutKey(
        row.Layout || row.layout,
        inferArticleRowLayoutFromColumnCount(countGuess)
    );
    const columnCount = getArticleRowLayoutColumnCount(layout);

    return {
        ColumnCount: columnCount,
        Columns: normalizeArticleColumns(columnsRaw, columnCount),
        ID: row.ID || makeArticleElementId(),
        Layout: layout,
        Position: getSafePosition(row.Position, index + 1),
    };
};

const normalizeArticleRows = (rows = []) => {
    return [...rows]
        .map(normalizeArticleRow)
        .sort((firstRow, secondRow) => firstRow.Position - secondRow.Position)
        .map((row, index) => ({
            ...row,
            Position: index + 1,
        }));
};

const normalizeApiResponse = (response) => {
    return response && response.data !== undefined ? response.data : response;
};

const normalizeApiDate = (value) => {
    if (!value || String(value).startsWith('0001-01-01')) return undefined;
    return value;
};

const isApiArticleId = (id) => {
    const numericId = Number(id);
    return Number.isInteger(numericId) && numericId > 0;
};

const parseArticleRowsFromApi = (rowsValue) => {
    if (Array.isArray(rowsValue)) {
        return rowsValue;
    }

    if (!rowsValue || rowsValue === '[]') {
        return [];
    }

    try {
        const parsed = JSON.parse(rowsValue);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const stringifyArticleRowsForApi = (rows = []) => {
    return JSON.stringify(rows);
};

const normalizeArticle = (article = {}, options = {}) => {
    const now = new Date().toISOString();
    const parsedRows = parseArticleRowsFromApi(article.Rows ?? article.rows);

    const base = {
        ...article,
        AuthorName: String(article.AuthorName || article.authorName || '').trim(),
        AuthorUserID: article.AuthorUserID ?? article.authorUserID,
        CreatedAt: normalizeApiDate(article.CreatedAt) || article.CreatedAt || now,
        ID: article.ID || makeLocalID(),
        PublishedDate: article.PublishedDate ?? article.publishedDate,
        Rows: normalizeArticleRows(parsedRows),
        SearchText: String(article.SearchText || article.searchText || '').trim(),
        Slug: sanitizeArticleSlugInput(article.Slug || article.slug || ''),
        Title: normalizeTitle(article.Title),
        UpdatedAt: options.touchUpdatedAt
            ? now
            : normalizeApiDate(article.UpdatedAt) || article.UpdatedAt || article.CreatedAt || now,
    };

    return {
        ...base,
        SearchText: base.SearchText || buildArticleSearchText(base),
    };
};

/** Normalizes a list item returned by the CMS search endpoint. */
const normalizeArticleSummary = (article = {}) => {
    return normalizeArticle({
        ...article,
        Rows: [],
    });
};

/** Normalizes a full article payload returned by the CMS detail endpoint. */
const normalizeArticleDetail = (article = {}) => {
    return normalizeArticle(article);
};

const buildArticleSearchPayload = (filters = {}) => {
    const text = filters.text ?? filters.Text;
    const userCreated = filters.userCreated ?? filters.UserCreated;

    return {
        Text: text ? String(text).trim() : null,
        UserCreated: userCreated ?? null,
    };
};

const toApiPublishedDate = (value) => {
    if (!value) return null;

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return parsedDate.toISOString();
};

const sortArticlesByCreatedAtDesc = (articles = []) => {
    return [...articles].sort((firstArticle, secondArticle) => {
        const firstCreatedAt = new Date(firstArticle.CreatedAt || 0).getTime();
        const secondCreatedAt = new Date(secondArticle.CreatedAt || 0).getTime();
        return secondCreatedAt - firstCreatedAt;
    });
};

const stripArticleMediaForStorage = (media = {}) => {
    return makePersistedArticleMedia(media);
};

const normalizeArticleForSave = (article = {}, options = {}) => {
    const normalizedArticle = normalizeArticle(article, options);
    const rows = normalizedArticle.Rows.map((row) => ({
        ...row,
        Columns: row.Columns.map((column) => ({
            ...column,
            Blocks: column.Blocks.map((block) => {
                if (!blockTypeHasMedia(block.Type) || !block.Media) return block;
                return {
                    ...block,
                    Media: stripArticleMediaForStorage(block.Media),
                };
            }),
        })),
    }));

    return {
        ...normalizedArticle,
        Rows: rows,
    };
};

const buildArticleSavePayload = (article = {}) => {
    const normalizedArticle = normalizeArticleForSave(article, { touchUpdatedAt: true });

    return {
        AuthorName: normalizedArticle.AuthorName,
        ID: isApiArticleId(normalizedArticle.ID) ? Number(normalizedArticle.ID) : 0,
        PublishedDate: toApiPublishedDate(normalizedArticle.PublishedDate),
        Rows: stringifyArticleRowsForApi(normalizedArticle.Rows),
        SearchText: normalizedArticle.SearchText,
        Slug: normalizedArticle.Slug,
        Title: normalizedArticle.Title,
    };
};

export const hasArticleMediaDocument = (media = {}) => {
    return Boolean(getArtMediaDocumentId(media));
};

export const searchArticlesApi = async (filters = {}) => {
    const response = await Axios.post(ARTICLES_SEARCH_API, buildArticleSearchPayload(filters));
    const articles = normalizeApiResponse(response);

    if (!Array.isArray(articles)) {
        return [];
    }

    return sortArticlesByCreatedAtDesc(articles.map((article) => normalizeArticleSummary(article)));
};

export const loadArticleById = async (articleId) => {
    const response = await Axios.get(`${ARTICLES_API_BASE}/${articleId}`);
    return normalizeArticleDetail(normalizeApiResponse(response));
};

export const saveArticleApi = async (article) => {
    const payload = buildArticleSavePayload(article);
    let savedId = isApiArticleId(article.ID) ? Number(article.ID) : null;

    if (savedId) {
        await Axios.put(`${ARTICLES_API_BASE}/${savedId}`, payload);
    } else {
        const response = await Axios.post(ARTICLES_API_BASE, payload);
        const saved = normalizeApiResponse(response);
        savedId = saved?.ID ?? saved?.id;
    }

    if (!savedId) {
        throw new Error('Invalid article save response');
    }

    return loadArticleById(savedId);
};

export const removeArticleApi = async (articleID) => {
    await Axios.delete(`${ARTICLES_API_BASE}/${articleID}`);
};

export const loadArticles = async (_mode, filters) => searchArticlesApi(filters);

export const saveArticle = async (article) => saveArticleApi(article);

export const removeArticle = async (articleID) => removeArticleApi(articleID);
